/**
 * HeroImageSectionFrontTall.jsx
 * Created by Destin Frasier on 06/18/20.
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import * as PartnerIcons from './icons/partners/PartnerIcons';

import Heading from './Heading';
import { ContentBlock } from './LayoutPrimitives';
import { useWindowSize } from '../../hooks';
import FauxImage, { FauxUnavailableImage } from './FauxImage';

export default function HeroImageSectionFrontTall({
    className,
    title,
    semanticElementName,
    partnerAgency,
    locationState,
    totalCampgrounds,
    totalActivities,
    totalTreePermits,
    totalActivityPasses,
    totalPOI,
    imagePath
}) {

function GovAgency(name) {
    const Partners = {
        BEP: {icon: <PartnerIcons.BEP />, name: "Bureau of Engraving and Printing (BEP) |"},
        BLM: {icon: <PartnerIcons.BLM />, name: "Bureau of Land Management (BLM) |"},
        DOI: {icon: <PartnerIcons.DOI />, name: "U.S. Department of Transportation (USDOT) |"},
        DOT: {icon: <PartnerIcons.DOT />, name: "Bureau of Reclamation |"},
        NPS: {icon: <PartnerIcons.NPS />, name: "National Park Service (NPS) |"},
        NOAA: {icon: <PartnerIcons.NOAA />, name: "National Oceanic and Atmospheric Administration (NOAA) |"},
        Smithsonian: {icon: <PartnerIcons.Smithsonian />, name: "Smithsonian |"},
        TRE: {icon: <PartnerIcons.TRE />, name: "Deptartment of The Treasury |"},
        TVA: {icon: <PartnerIcons.TVA />, name: "Tennessee Valley Authority |"},
        USACE: {icon: <PartnerIcons.USACE />, name: "Army Corps of Engineers (USACE) |"},
        USFS: {icon: <PartnerIcons.ForestService />, name: "U.S. Forest Service (USFS) |"},
        USFWS: {icon: <PartnerIcons.FWS />, name: "U.S. Fish & Wildlife (USFWS) |"},
        none: ''
    }

    return Partners[name] || Partners.none
}

const { width } = useWindowSize();
const agencyInfo = GovAgency(`${partnerAgency}`);
const SemanticElementName = semanticElementName;
const hasInventory = (totalCampgrounds + totalActivities + totalPOI + totalActivityPasses + totalTreePermits);

const setImageHeight = (width >= 768 ? 500 : 350);

return (
    <SemanticElementName
        role="img"
        aria-label={title}
        className={cx('rec-section-tall-hero-full-width-wrap', className)}>
        <ContentBlock>
            <div className="rec-section-tall-hero-body">
                <div className="rec-section-tall-hero-location-info">
                    {agencyInfo.icon} {agencyInfo.name} {locationState}
                </div>

                <Heading
                    headingLevel={1}
                    appearance="h1"
                    className={cx('rec-section-tall-hero-title', {
                        'rec-long-hero-title': title.length > 30
                    })}>
                    {title}
                </Heading>
                {hasInventory >= 1 && (
                    <ul className="rec-section-tall-hero-inventory-list">
                        {totalCampgrounds > 0 && (
                            <li>{totalCampgrounds} {totalCampgrounds > 1 ? "Campgrounds" : "Campground"}</li>
                        )}
                        {totalActivities > 0 && (
                            <li>{totalActivities} {totalActivities > 1 ? "Activities" : "Activity"}</li>
                        )}
                        {totalTreePermits > 0 && (
                            <li>{totalTreePermits} {totalTreePermits > 1 ? "Tree Permits" : "Tree Permit"}</li>
                        )}
                        {totalActivityPasses > 0 && (
                            <li>{totalActivityPasses} {totalActivityPasses > 1 ? "Activity Passes" : "Activity Pass"}</li>
                        )}
                        {totalPOI > 0 && (
                            <li>{totalPOI} {totalPOI > 1 ? "Points of Interest" : "Point of Interest"}</li>
                        )}
                    </ul>
                )}
            </div>

        </ContentBlock>

        {imagePath === undefined ? (
            <FauxUnavailableImage
            style={{ minHeight: `${setImageHeight}px`}} />
        ) : (
        <FauxImage
            src={imagePath}
            altText={title}
            style={{ minHeight: `${setImageHeight}px`}} />
        )}
    </SemanticElementName>
    );
}

HeroImageSectionFrontTall.propTypes = {
    /** Title of the page/hero image */
    title: PropTypes.string,

    /** Added to existing classes */
    className: PropTypes.string,

    /** Set the landmark or html tag for the component */
    semanticElementName: PropTypes.oneOf(['nav', 'main', 'section', 'div']),

    /**  Managing government agency */
    partnerAgency:PropTypes.oneOf(['BEP', 'BLM', 'DOI', 'DOT', 'NPS', 'NOAA', 'Smithsonian', 'TRE', 'TVA', 'USACE', 'USFS', 'USFWS']),

    /** The state where this inventory is located */
    locationState: PropTypes.string,

    /** Total number of campgrounds */
    totalCampgrounds: PropTypes.number,

    /** Total number of activities */
    totalActivities: PropTypes.number,

    /** Total number of activity passes */
    totalActivityPasses: PropTypes.number,

    /** Total number of tree permits */
    totalTreePermits: PropTypes.number,

    /** Total number of POI */
    totalPOI:PropTypes.number,

    /** Path for the background image */
    imagePath:PropTypes.string
};

HeroImageSectionFrontTall.defaultProps = {
    semanticElementName: 'div'
};